import { CUSTOMER_ROLES } from '@misumi/shared'
import type { CustomerRole } from '@misumi/shared'
import { useState } from 'react'
import type { components } from '../../lib/api/schema.gen'
import { Select } from '../ui/Select'
import { Table } from '../ui/Table'
import { roleLabel } from './roleLabels'

type JoinRequestView = components['schemas']['JoinRequestView']

export interface JoinRequestsTableProps {
  readonly joinRequests: readonly JoinRequestView[]
  readonly decidingId: string | undefined
  readonly onApprove: (id: string, role: CustomerRole) => void
  readonly onDeny: (id: string) => void
}

/**
 * Pending join-requests table (F0.6, PR-ONB-01) — `GET
 * /onboarding/join-requests` (S0.10), admin-only, RLS-scoped. Approve grants
 * a role chosen from an inline per-row `<select>` (defaulting to `buyer`,
 * matching the server's own default when no `role` is sent — see
 * `ApproveJoinRequestRequest`), never an escalation the server wouldn't
 * already allow (`isCustomerRole` ceiling, ADR-0021). Self-approval is
 * refused server-side (C13, 403 `self_approval`) — the caller cannot even
 * identify which row is "their own request" from this view alone (no
 * self-flag in `JoinRequestView`), so that guard is intentionally NOT
 * duplicated client-side (would require guessing at "current user" matching
 * logic the contract doesn't expose here) and is surfaced via the server's
 * error message if it happens.
 */
export function JoinRequestsTable({
  joinRequests,
  decidingId,
  onApprove,
  onDeny,
}: JoinRequestsTableProps) {
  // Base the empty state on the same filtered set the rows render — a list of
  // only non-pending requests must still show the empty message, not an
  // empty <table> with no rows.
  const pendingRequests = joinRequests.filter((joinRequest) => joinRequest.status === 'pending')
  return (
    <Table caption="Pending requests to join this account" columnCount={4}>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>Email</Table.HeaderCell>
          <Table.HeaderCell>Requested</Table.HeaderCell>
          <Table.HeaderCell>Grant role</Table.HeaderCell>
          <Table.HeaderCell>Actions</Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {pendingRequests.length === 0 && <Table.Empty title="No pending join requests." />}
        {pendingRequests.map((joinRequest) => (
          <JoinRequestRow
            key={joinRequest.id}
            joinRequest={joinRequest}
            deciding={decidingId === joinRequest.id}
            onApprove={onApprove}
            onDeny={onDeny}
          />
        ))}
      </Table.Body>
    </Table>
  )
}

function JoinRequestRow({
  joinRequest,
  deciding,
  onApprove,
  onDeny,
}: {
  readonly joinRequest: JoinRequestView
  readonly deciding: boolean
  readonly onApprove: (id: string, role: CustomerRole) => void
  readonly onDeny: (id: string) => void
}) {
  const [role, setRole] = useState<CustomerRole>('buyer')
  const selectId = `grant-role-${joinRequest.id}`

  return (
    <Table.Row>
      <Table.Cell>{joinRequest.requesterEmail}</Table.Cell>
      <Table.Cell>{formatDate(joinRequest.createdAt)}</Table.Cell>
      <Table.Cell>
        <Select
          id={selectId}
          label={`Role to grant ${joinRequest.requesterEmail}`}
          hideLabel
          size="sm"
          value={role}
          onChange={(event) => setRole(event.target.value as CustomerRole)}
          disabled={deciding}
        >
          {CUSTOMER_ROLES.map((candidateRole) => (
            <option key={candidateRole} value={candidateRole}>
              {roleLabel(candidateRole)}
            </option>
          ))}
        </Select>
      </Table.Cell>
      <Table.Cell>
        <div className="row-actions">
          <button
            type="button"
            className="btn-primary"
            disabled={deciding}
            onClick={() => onApprove(joinRequest.id, role)}
          >
            {deciding ? 'Working…' : 'Approve'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={deciding}
            onClick={() => onDeny(joinRequest.id)}
          >
            Deny
          </button>
        </div>
      </Table.Cell>
    </Table.Row>
  )
}

function formatDate(isoDate: string): string {
  const parsed = new Date(isoDate)
  if (Number.isNaN(parsed.getTime())) {
    return '—'
  }
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}
